/**
 * Fallback USD/token prices when `/api/rounds/current` omits next/listing
 * (matches previous product copy; override via `nextTokenPrice` / `listingTokenPrice`).
 */
export const FALLBACK_NEXT_TOKEN_PRICE_USD = 0.045;
export const FALLBACK_LISTING_TOKEN_PRICE_USD = 0.15;

/** Coerce API values (number | numeric string | nullish) for display & math. */
export function toFiniteNumber(value: unknown): number | undefined {
  if (value == null) return undefined;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** USD/token display for presale cards (aligned with dashboard `currentRound`). */
export function formatPresaleUsdPrice(value: unknown): string {
  const n = toFiniteNumber(value);
  if (n == null) return "—";
  const abs = Math.abs(n);
  const decimals = abs > 0 && abs < 0.01 ? 5 : abs < 1 ? 4 : abs < 100 ? 3 : 2;
  return n.toFixed(decimals);
}

export function formatPresaleUsdLabel(value: unknown): string {
  const inner = formatPresaleUsdPrice(value);
  return inner === "—" ? "—" : `$${inner}`;
}

/** Current-round token price: `$0.00` when API value is missing or invalid. */
export function formatPresaleCurrentUsdLabel(value: unknown): string {
  const n = toFiniteNumber(value);
  if (n == null) return "$0.00";
  return formatPresaleUsdLabel(n);
}

export function formatUsdCompact(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(0)}`;
}
